<!--
// nuxt-ui/components/pages/region-tier-list/LayoutTierListKwcClubs.vue
-->
<script setup lang="ts">
import type { TierListTeam } from "~/src/tier-list/domain/models/TierList";
import draggable from "vuedraggable";
import KleTeams from "~/src/tier-list/datasources/kle-kwc.json";
import KlaTeams from "~/src/tier-list/datasources/kla-kwc.json";
import RedemptionTeams from "~/src/tier-list/datasources/redemption-kwc.json";
import WildcardTeams from "~/src/tier-list/datasources/wildcards-kwc.json";
import LineStageDraggableKwc from "~/nuxt-ui/components/draggable/LineStageDraggableKwc.vue";
import ResetIcon from "~/nuxt-ui/components/Shared/icons/ResetIcon.vue";
import DownloadIcon from "~/nuxt-ui/components/Shared/icons/DownloadIcon.vue";

const { t } = useI18n();

const pageIsActive = false;

const kleItems = ref<TierListTeam[]>([...KleTeams]);
const klaItems = ref<TierListTeam[]>([...KlaTeams]);
const redemptionItems = ref<TierListTeam[]>([...RedemptionTeams]);
const wildcardItems = ref<TierListTeam[]>([...WildcardTeams]);

const listOutRedemption = ref([]);
const list02 = ref([]);
const listLastChance = ref([]);
const listRound16 = ref([]);
const listQuarterFinals = ref([]);
const listFinals = ref([]);

const drag = ref(false);

const KWCLogoDate = "/img/world-cup/KWC-clubs-horizontal.png";
const KWCWebText = "/img/world-cup/kwc-web-text.png";

const allDraggedTeams = computed(() => {
  return [
    ...listOutRedemption.value,
    ...list02.value,
    ...listLastChance.value,
    ...listRound16.value,
    ...listQuarterFinals.value,
    ...listFinals.value,
  ];
});

const draggedSpainTeams = computed(() => {
  return allDraggedTeams.value.length ? allDraggedTeams.value.filter(t => t.kwcGroup === "spain") : [];
});

const draggedAmericasTeams = computed(() => {
  return allDraggedTeams.value.length ? allDraggedTeams.value.filter(t => t.kwcGroup === "americas") : [];
});

const draggedRedemptionTeams = computed(() => {
  return allDraggedTeams.value.length ? allDraggedTeams.value.filter(t => t.kwcGroup === "redemption") : [];
});

const draggedWildcardTeams = computed(() => {
  return allDraggedTeams.value.length ? allDraggedTeams.value.filter(t => t.kwcGroup === "wildcards") : [];
});

const resetTierList = () => {
  kleItems.value = [...KleTeams];
  klaItems.value = [...KlaTeams];
  redemptionItems.value = [...RedemptionTeams];
  wildcardItems.value = [...WildcardTeams];

  listOutRedemption.value = [];
  list02.value = [];
  listLastChance.value = [];
  listRound16.value = [];
  listQuarterFinals.value = [];
  listFinals.value = [];
};

const capture = () => {
  if (typeof window !== "undefined" && window.html2canvas) {
    const htmlSection: HTMLElement = document.getElementById("TierList");
    setTimeout(function () {
      window
        .html2canvas(htmlSection, {
          allowTaint: true,
          useCORS: true,
          scale: 1.2,
          onclone: function (screenshot) {
            const container = screenshot.querySelector("#TierList");
            container.classList.remove("hidden");

            const titles = screenshot.querySelectorAll(".container-line-title-static");
            titles.forEach(title => {
              title.style.paddingTop = "5px";
              title.style.paddingBottom = "20px";
            });
          },
        })
        .then(function (canvas) {
          const imageData = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = imageData;
          link.download = "tierKwc.png";
          link.click();
          link.remove();
        })
        .catch(function (e) {
          console.error("Error generating screenshot:", e);
        });
    }, 200);
  }
};

const tooltipOptions = (tooltipText: string) => {
  return {
    value: tooltipText,
    pt: {
      arrow: {
        style: {
          borderBottomColor: "#525252",
        },
      },
      text: "bg-gray-700 !text-xs text-white rounded-md px-1.5 py-1",
    },
  };
};

useSeoMeta({
  title: t("worldCup.tierList.title", { kwcType: "Clubs" }),
  ogTitle: t("worldCup.tierList.ogTitle", { kwcType: "Clubs" }),
  ogDescription: t("worldCup.tierList.description"),
  description: t("worldCup.tierList.description"),
});
</script>

<template>
  <section class="pb-6 min-w-full min-h-full main-container">
    <div>
      <div
        class="flex flex-col bigsm:flex-row justify-start items-center gap-3 bigsm:gap-9 px-4 pt-4 w-full max-w-full lg:-mb-8"
      >
        <img
          :src="KWCLogoDate"
          alt="Kings World Cup from May 26th to June 8th, Mexico"
          width="229px"
          height="auto"
          class="w-[150px] md:w-[190px]"
        />
        <div class="text-center bigsm:text-left text-base bigsm:text-xl">
          <div class="font-black md:flex md:gap-2">
            <p>{{ t("worldCup.tierList.predictions.text") }}</p>
            <h1 class="inline">KWC Tier List</h1>
            <span>!</span>
          </div>
          <p v-if="pageIsActive">{{ t("worldCup.tierList.predictions.download") }}</p>
        </div>
        <!--        <img-->
        <!--          :src="KWCWebText"-->
        <!--          alt="kingsleague.pro/kwc"-->
        <!--          width="229px"-->
        <!--          height="auto"-->
        <!--          class="w-[210px] md:w-[390px]"-->
        <!--        />-->
      </div>
      <div class="px-3.5 md:px-6 py-4 2xl:pt-10 max-w-[970px] mx-auto">
        <line-stage-draggable-kwc
          stage-title="Finals"
          :list-length="listFinals.length"
          :max-items="4"
          bg-title-color="bg-[#D4B78E]"
        >
          <draggable
            v-model="listFinals"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: to => {
                return to.el.children.length < 4;
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                :data-kwc="element.kwcGroup"
                draggable="false"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>
        <line-stage-draggable-kwc
          stage-title="Quarter Finals"
          :list-length="listQuarterFinals.length"
          :max-items="4"
          bg-title-color="bg-[#D9AC56]"
        >
          <draggable
            v-model="listQuarterFinals"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: to => {
                return to.el.children.length < 4;
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Round of 16"
          :list-length="listRound16.length"
          :max-items="8"
          bg-title-color="bg-[#E39451]"
        >
          <draggable
            v-model="listRound16"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: to => {
                return to.el.children.length < 8;
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Last Chance"
          :list-length="listLastChance.length"
          :max-items="8"
          bg-title-color="bg-[#DB6E31]"
        >
          <draggable
            v-model="listLastChance"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: to => {
                return to.el.children.length < 8;
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="0 - 2"
          :list-length="list02.length"
          :max-items="8"
          bg-title-color="bg-[#C12F2F]"
        >
          <draggable
            v-model="list02"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: to => {
                return to.el.children.length < 8;
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Out at redemption"
          :list-length="listOutRedemption.length"
          :max-items="4"
          bg-title-color="bg-[#B90101]"
        >
          <draggable
            v-model="listOutRedemption"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'teams',
              pull: true,
              put: (to, from, evt) => {
                return to.el.children.length < 4 && evt.dataset.kwc === 'redemption';
              },
            }"
            class="assigned-line-container"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="assigned-team-logo"
              />
            </template>
          </draggable>
        </line-stage-draggable-kwc>
      </div>
    </div>

    <div
      class="kql-w-container overflow-x-scroll max-w-full bg-black bigsm:bg-transparent md:bg-none py-6 sticky bottom-0 md:block md:bottom-auto"
    >
      <div
        v-if="pageIsActive"
        class="flex md:grid md:grid-cols-4 gap-4 place-items-start justify-items-start md:justify-items-center min-w-max md:min-w-full"
      >
        <div class="container-group-draggables">
          <img
            src="/img/tier-list-kwc/kle-gold.png"
            alt="Kings League InfoJobs"
            class="md:mx-auto md:mb-3 mr-3 w-32 lg:w-auto"
          />
          <draggable
            v-model="kleItems"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'kle',
              pull: true,
              put: (to, from, evt) => {
                return evt.dataset.kwc === 'spain';
              },
            }"
            :sort="false"
            class="grid grid-cols-4 place-items-center gap-2"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="pending-assign-team-logo"
              />
            </template>
            <template #footer>
              <img
                v-for="team in draggedSpainTeams"
                :key="team.id"
                :src="team.logo"
                :alt="team.name"
                draggable="false"
                class="assigned-team-logo-ghost"
              />
            </template>
          </draggable>
        </div>

        <div class="container-group-draggables">
          <div class="md:w-full md:h-24 md:flex md:flex-col md:justify-end">
            <img
              src="/img/tier-list-kwc/redemption-gold.png"
              alt="Redemption"
              class="md:mx-auto md:mb-6 mr-3 w-32 lg:w-auto"
            />
          </div>
          <draggable
            v-model="redemptionItems"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'redemption',
              pull: true,
              put: (to, from, evt) => {
                return evt.dataset.kwc === 'redemption';
              },
            }"
            :sort="false"
            class="grid grid-cols-4 place-items-center gap-2"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="pending-assign-team-logo"
              />
            </template>
            <template #footer>
              <img
                v-for="team in draggedRedemptionTeams"
                :key="team.id"
                :src="team.logo"
                :alt="team.name"
                draggable="false"
                class="assigned-team-logo-ghost"
              />
            </template>
          </draggable>
        </div>

        <div class="container-group-draggables">
          <img
            src="/img/tier-list-kwc/kla-gold.png"
            alt="Américas Kings League Santander"
            class="md:mx-auto md:mb-3 mr-3 w-32 lg:w-auto"
          />
          <draggable
            v-model="klaItems"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'kla',
              pull: true,
              put: (to, from, evt) => {
                return evt.dataset.kwc === 'americas';
              },
            }"
            :sort="false"
            class="grid grid-cols-4 place-items-center gap-2"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="pending-assign-team-logo"
              />
            </template>
            <template #footer>
              <img
                v-for="team in draggedAmericasTeams"
                :key="team.id"
                :src="team.logo"
                :alt="team.name"
                draggable="false"
                class="assigned-team-logo-ghost"
              />
            </template>
          </draggable>
        </div>

        <div class="container-group-draggables">
          <img
            src="/img/tier-list-kwc/wildcards-gold.png"
            alt="Wildcards"
            class="md:mx-auto md:mb-3 mr-3 w-32 lg:w-auto"
          />
          <draggable
            v-model="wildcardItems"
            item-key="id"
            @start="drag = true"
            @end="drag = false"
            ghost-class="item-draggable-ghost"
            :group="{
              name: 'wildcards',
              pull: true,
              put: (to, from, evt) => {
                return evt.dataset.kwc === 'wildcards';
              },
            }"
            :sort="false"
            class="grid grid-cols-6 md:grid-cols-4 place-items-center gap-2"
          >
            <template #item="{ element }">
              <img
                v-tooltip.top="tooltipOptions(element.name)"
                :src="element.logo"
                :alt="element.name"
                draggable="false"
                :data-kwc="element.kwcGroup"
                class="pending-assign-team-logo"
              />
            </template>
            <template #footer>
              <img
                v-for="team in draggedWildcardTeams"
                :key="team.id"
                :src="team.logo"
                :alt="team.name"
                draggable="false"
                class="assigned-team-logo-ghost"
              />
            </template>
          </draggable>
        </div>
      </div>
      <div v-else class="text-center italic">{{ t("worldCup.tierList.noTeams") }}</div>
    </div>
  </section>

  <section v-if="pageIsActive" id="TierList" class="main-container pt-6 px-6 pb-10 w-[1200px] h-auto hidden">
    <div>
      <div class="flex justify-between items-center gap-3 px-4 pt-4 w-full max-w-full -mb-10">
        <img
          :src="KWCLogoDate"
          alt="Kings World Cup from May 26th to June 8th, Mexico"
          width="150px"
          height="auto"
          class="w-[180px]"
        />
        <img :src="KWCWebText" alt="kingsleague.pro/kwc" width="230px" height="auto" class="w-[230px]" />
      </div>
      <div class="px-3.5 md:px-6 py-4 2xl:pt-10 max-w-[915px] mx-auto">
        <line-stage-draggable-kwc
          stage-title="Finals"
          :list-length="listFinals.length"
          :max-items="4"
          bg-title-color="bg-[#D4B78E]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in listFinals"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Quarter Finals"
          :list-length="listQuarterFinals.length"
          :max-items="4"
          bg-title-color="bg-[#D9AC56]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in listQuarterFinals"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Round of 16"
          :list-length="listRound16.length"
          :max-items="8"
          bg-title-color="bg-[#E39451]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in listRound16"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Last Chance"
          :list-length="listLastChance.length"
          :max-items="8"
          bg-title-color="bg-[#DB6E31]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in listLastChance"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="0 - 2"
          :list-length="list02.length"
          :max-items="8"
          bg-title-color="bg-[#C12F2F]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in list02"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>

        <line-stage-draggable-kwc
          stage-title="Out at redemption"
          :list-length="listOutRedemption.length"
          :max-items="4"
          bg-title-color="bg-[#B90101]"
          is-canvas-image
        >
          <div class="assigned-line-container-static">
            <img
              v-for="element in listOutRedemption"
              :src="element.logo"
              :alt="element.name"
              :data-kwc="element.kwcGroup"
              class="assigned-team-logo"
            />
          </div>
        </line-stage-draggable-kwc>
      </div>
    </div>
  </section>

  <button
    v-if="pageIsActive"
    class="btn btn-default flex items-center justify-start gap-2 fixed -right-1 bigsm:-right-2 top-1/4"
    @click="resetTierList"
  >
    <ResetIcon />
    <span class="hidden md:inline-block">{{ t("worldCup.tierList.button.restart") }}</span>
  </button>
  <button
    v-if="pageIsActive"
    class="btn btn-emphasis flex items-center justify-start gap-2 fixed -right-1 bigsm:-right-2 top-[calc(25%+60px)]"
    @click="capture"
  >
    <DownloadIcon />
    <span class="hidden md:inline-block">{{ t("worldCup.tierList.button.download") }}</span>
  </button>
</template>

<style scoped>
.assigned-line-container {
  @apply min-h-[120px] bigsm:min-h-[60px] min-w-full flex justify-start flex-wrap items-start max-w-[270px] mx-auto;
  @apply bigsm:mx-0 bigsm:max-w-full bigsm:items-center gap-x-2.5 md:gap-x-4 absolute;
}

.assigned-line-container-static {
  @apply min-h-[60px] min-w-full flex justify-start max-w-full items-center gap-x-4 absolute;
}

.assigned-team-logo {
  @apply w-[60px] h-[60px] object-contain object-center cursor-pointer;
}

.pending-assign-team-logo {
  @apply w-[50px] h-[50px] md:w-[60px] md:h-[60px] object-contain object-center cursor-pointer;
}

.assigned-team-logo-ghost {
  @apply opacity-30 pending-assign-team-logo cursor-none;
}

.item-draggable-ghost {
  @apply cursor-grabbing;
}

.container-group-draggables {
  @apply flex md:flex-col justify-center items-center px-4 border-r border-r-gray-700 md:border-none;
}

.main-container {
  background-image: url("/img/tier-list-kwc/bg-kwc-tier-list.jpg");
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
}
</style>
